import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Previewer } from "../DocumentPreviewer/Previewer";
import { getFileExtension, imageTypes } from "../DocumentPreviewer/utils";

/* ---------- icons (inline SVG, no emoji) ---------- */
const Icon = ({ d, size = 20, stroke = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
const IcZoomIn = () => <Icon d={<><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" /></>} />;
const IcZoomOut = () => <Icon d={<><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" /></>} />;
const IcReset = () => <Icon d={<><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" /></>} />;
const IcRotate = () => <Icon d={<><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></>} />;
const IcDownload = () => <Icon d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>} />;
const IcClose = () => <Icon d={<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>} size={22} />;
const IcLeft = () => <Icon d={<polyline points="15 18 9 12 15 6" />} size={26} />;
const IcRight = () => <Icon d={<polyline points="9 18 15 12 9 6" />} size={26} />;
const IcEye = () => <Icon d={<><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></>} />;
const IcFile = ({ size = 30 }) => <Icon size={size} stroke={1.6} d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>} />;

const MIN = 1, MAX = 6, STEP = 0.35;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const baseName = (s) => { try { return decodeURIComponent(String(s).split("/").pop()); } catch { return String(s).split("/").pop(); } };
const isImage = (ext) => imageTypes.includes((ext || "").toLowerCase());

/* injected once: animations + reduced-motion */
const STYLE_ID = "dpm-anim";
function ensureStyles() {
  if (typeof document === "undefined" || document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
  @keyframes dpmFade{from{opacity:0}to{opacity:1}}
  @keyframes dpmPop{from{opacity:0;transform:scale(.96)}to{opacity:1;transform:scale(1)}}
  .dpm-backdrop{animation:dpmFade .18s ease-out}
  .dpm-shell{animation:dpmPop .22s cubic-bezier(.2,.8,.2,1)}
  .dpm-btn{transition:background .15s ease,color .15s ease,transform .12s ease,opacity .15s ease}
  .dpm-btn:hover:not(:disabled){background:rgba(255,255,255,.16)}
  .dpm-btn:active:not(:disabled){transform:scale(.94)}
  .dpm-btn:focus-visible{outline:2px solid #38e0b8;outline-offset:2px}
  .dpm-nav:hover{background:rgba(255,255,255,.22)}
  .dpm-tile{transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease}
  .dpm-tile:hover{transform:translateY(-2px);box-shadow:0 10px 26px rgba(15,23,42,.16);border-color:#94a3b8}
  .dpm-tile:hover .dpm-tileov{opacity:1}
  .dpm-tileov{transition:opacity .18s ease}
  @media (prefers-reduced-motion: reduce){
    .dpm-backdrop,.dpm-shell{animation:none}
    .dpm-btn,.dpm-tile,.dpm-tileov{transition:none}
  }`;
  document.head.appendChild(el);
}

/* ---------- the modal ---------- */
export default function DocumentPreviewModal({ isOpen, items = [], startIndex = 0, resolveUrl, onClose, onDownload }) {
  const [index, setIndex] = useState(startIndex);
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [scale, setScale] = useState(1);
  const [rot, setRot] = useState(0);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  const stageRef = useRef(null);
  const closeRef = useRef(null);
  const pointers = useRef(new Map());
  const pinch = useRef({ dist: 0 });
  const drag = useRef(null);
  const reqId = useRef(0);

  const count = items.length;
  const item = items[index] || null;
  const ext = item ? (item.ext || getFileExtension(item.doc || item.name || "")) : null;
  const imageMode = isImage(ext);

  useEffect(() => { ensureStyles(); }, []);
  useEffect(() => { if (isOpen) setIndex(clamp(startIndex, 0, Math.max(0, count - 1))); }, [isOpen, startIndex, count]);

  const resetView = useCallback(() => { setScale(1); setRot(0); setTx(0); setTy(0); }, []);

  /* resolve signed url for current item */
  useEffect(() => {
    if (!isOpen || !item) return;
    let alive = true;
    const myReq = ++reqId.current;
    resetView();
    setStatus("loading");
    setUrl("");
    Promise.resolve(resolveUrl ? resolveUrl(item.doc, index) : item.doc)
      .then((u) => { if (alive && myReq === reqId.current) { setUrl(u); setStatus("ready"); } })
      .catch(() => { if (alive && myReq === reqId.current) setStatus("error"); });
    return () => { alive = false; };
  }, [isOpen, index, item, resolveUrl, resetView]);

  /* body scroll lock + focus */
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => closeRef.current && closeRef.current.focus(), 30);
    return () => { document.body.style.overflow = prev; clearTimeout(t); };
  }, [isOpen]);

  const go = useCallback((dir) => { if (count > 1) setIndex((i) => (i + dir + count) % count); }, [count]);
  const zoom = useCallback((delta) => setScale((s) => { const n = clamp(+(s + delta).toFixed(2), MIN, MAX); if (n === 1) { setTx(0); setTy(0); } return n; }), []);

  /* keyboard */
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "+" || e.key === "=") zoom(STEP);
      else if (e.key === "-" || e.key === "_") zoom(-STEP);
      else if (e.key === "0") resetView();
      else if (e.key.toLowerCase() === "r") setRot((r) => (r + 90) % 360);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, go, zoom, resetView, onClose]);

  /* non-passive wheel zoom on the stage */
  useEffect(() => {
    const el = stageRef.current;
    if (!el || !imageMode) return;
    const onWheel = (e) => { e.preventDefault(); zoom(e.deltaY < 0 ? STEP : -STEP); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [imageMode, zoom, status]);

  /* pointer pan + pinch */
  const onPointerDown = (e) => {
    if (!imageMode) return;
    e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current.dist = Math.hypot(a.x - b.x, a.y - b.y);
    } else if (scale > 1) {
      drag.current = { x: e.clientX, y: e.clientY, tx, ty };
    }
  };
  const onPointerMove = (e) => {
    if (!imageMode || !pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch.current.dist) zoom(((dist - pinch.current.dist) / 200) * STEP * 3);
      pinch.current.dist = dist;
    } else if (drag.current && scale > 1) {
      setTx(drag.current.tx + (e.clientX - drag.current.x));
      setTy(drag.current.ty + (e.clientY - drag.current.y));
    }
  };
  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current.dist = 0;
    if (pointers.current.size === 0) drag.current = null;
  };

  if (!isOpen) return null;

  const C = {
    backdrop: { position: "fixed", inset: 0, zIndex: 100000, background: "rgba(8,12,20,.86)", backdropFilter: "blur(4px)", display: "flex", flexDirection: "column" },
    bar: { display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", color: "#e8edf5", flex: "none" },
    title: { minWidth: 0, display: "flex", flexDirection: "column", lineHeight: 1.25 },
    name: { fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "46vw" },
    sub: { fontSize: 12, color: "#94a3b8", fontVariantNumeric: "tabular-nums" },
    spacer: { flex: 1 },
    btn: { width: 40, height: 40, display: "grid", placeItems: "center", borderRadius: 10, border: "1px solid rgba(255,255,255,.14)", background: "rgba(255,255,255,.06)", color: "#e8edf5", cursor: "pointer" },
    pct: { minWidth: 56, textAlign: "center", fontSize: 13, fontVariantNumeric: "tabular-nums", color: "#cbd5e1", userSelect: "none" },
    stage: { flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", touchAction: imageMode ? "none" : "auto", cursor: imageMode ? (scale > 1 ? "grab" : "zoom-in") : "default" },
    nav: { position: "absolute", top: "50%", transform: "translateY(-50%)", width: 46, height: 46, borderRadius: "50%", display: "grid", placeItems: "center", border: "none", background: "rgba(255,255,255,.12)", color: "#fff", cursor: "pointer", zIndex: 2 },
    footer: { flex: "none", display: "flex", justifyContent: "center", gap: 6, padding: "10px 0 16px" },
    dot: (a) => ({ width: a ? 22 : 8, height: 8, borderRadius: 999, border: "none", background: a ? "#38e0b8" : "rgba(255,255,255,.34)", cursor: "pointer", transition: "width .18s ease,background .18s ease" }),
  };

  const ToolBtn = ({ label, onClick, disabled, children, innerRef }) => (
    <button ref={innerRef} type="button" className="dpm-btn" aria-label={label} title={label}
      onClick={onClick} disabled={disabled} style={{ ...C.btn, opacity: disabled ? 0.4 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
      {children}
    </button>
  );

  return createPortal(
    <div className="dpm-backdrop" role="dialog" aria-modal="true" aria-label="Document preview" style={C.backdrop}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div className="dpm-shell" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }} onMouseDown={(e) => e.stopPropagation()}>
        {/* toolbar */}
        <div style={C.bar}>
          <div style={C.title}>
            <span style={C.name}>{item ? (item.name || baseName(item.doc)) : "Document"}</span>
            {count > 1 && <span style={C.sub}>{index + 1} of {count}</span>}
          </div>
          <div style={C.spacer} />
          {imageMode && (
            <>
              <ToolBtn label="Zoom out" onClick={() => zoom(-STEP)} disabled={scale <= MIN}><IcZoomOut /></ToolBtn>
              <span style={C.pct}>{Math.round(scale * 100)}%</span>
              <ToolBtn label="Zoom in" onClick={() => zoom(STEP)} disabled={scale >= MAX}><IcZoomIn /></ToolBtn>
              <ToolBtn label="Rotate" onClick={() => setRot((r) => (r + 90) % 360)}><IcRotate /></ToolBtn>
              <ToolBtn label="Reset view" onClick={resetView} disabled={scale === 1 && rot === 0 && tx === 0 && ty === 0}><IcReset /></ToolBtn>
            </>
          )}
          {onDownload && <ToolBtn label="Download" onClick={() => onDownload(item, index)}><IcDownload /></ToolBtn>}
          <ToolBtn label="Close preview" onClick={onClose} innerRef={closeRef}><IcClose /></ToolBtn>
        </div>

        {/* stage */}
        <div ref={stageRef} style={C.stage}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endPointer} onPointerCancel={endPointer}
          onDoubleClick={() => { if (imageMode) (scale > 1 ? resetView() : setScale(2.5)); }}>

          {count > 1 && <button type="button" className="dpm-nav" aria-label="Previous document" style={{ ...C.nav, left: 14 }} onClick={() => go(-1)}><IcLeft /></button>}
          {count > 1 && <button type="button" className="dpm-nav" aria-label="Next document" style={{ ...C.nav, right: 14 }} onClick={() => go(1)}><IcRight /></button>}

          {status === "loading" && <div style={{ color: "#cbd5e1", display: "flex", alignItems: "center", gap: 10 }}>
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Loading…</div>}

          {status === "error" && <div style={{ color: "#fca5a5", textAlign: "center" }}>
            <p style={{ marginBottom: 8 }}>Couldn’t load this document.</p>
            <button type="button" className="dpm-btn" style={{ ...C.btn, width: "auto", padding: "0 16px" }} onClick={() => setIndex((i) => i)}>Retry</button>
          </div>}

          {status === "ready" && url && (
            imageMode ? (
              <img src={url} alt={item ? (item.name || baseName(item.doc)) : "Document preview"} draggable={false}
                style={{ maxWidth: "92%", maxHeight: "92%", objectFit: "contain", userSelect: "none",
                  transform: `translate(${tx}px,${ty}px) scale(${scale}) rotate(${rot}deg)`,
                  transition: drag.current || pointers.current.size ? "none" : "transform .15s ease-out",
                  borderRadius: 6, boxShadow: "0 10px 40px rgba(0,0,0,.5)", willChange: "transform" }} />
            ) : (
              <div style={{ width: "92%", height: "92%", background: "#0f172a", borderRadius: 10, overflow: "hidden" }}>
                <Previewer signedUrl={url} type={ext} url={item && item.doc} height="100%" width="100%" />
              </div>
            )
          )}
        </div>

        {/* footer dots */}
        {count > 1 && (
          <div style={C.footer}>
            {items.map((_, i) => (
              <button key={i} type="button" aria-label={`Go to document ${i + 1}`} aria-current={i === index} style={C.dot(i === index)} onClick={() => setIndex(i)} />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/* ---------- thumbnail tile (beautiful card with Preview + Download) ---------- */
export function DocumentTile({ doc, label, resolveUrl, onPreview, onDownload }) {
  const ext = getFileExtension(doc) || "";
  const image = isImage(ext);
  const [thumb, setThumb] = useState("");
  const [thumbErr, setThumbErr] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!image) return;
    Promise.resolve(resolveUrl ? resolveUrl(doc) : doc)
      .then((u) => alive && setThumb(u))
      .catch(() => alive && setThumbErr(true));
    return () => { alive = false; };
  }, [doc, image, resolveUrl]);

  useEffect(() => { ensureStyles(); }, []);

  const card = { position: "relative", width: 132, borderRadius: 12, overflow: "hidden", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", textAlign: "left" };
  const media = { height: 96, display: "flex", alignItems: "center", justifyContent: "center", background: "#f1f5f9", color: "#64748b" };
  const ov = { position: "absolute", top: 0, left: 0, right: 0, height: 96, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: "rgba(15,23,42,.55)", opacity: 0 };
  const ovBtn = { width: 36, height: 36, borderRadius: 9, border: "none", background: "rgba(255,255,255,.92)", color: "#0f172a", display: "grid", placeItems: "center", cursor: "pointer" };
  const cap = { padding: "8px 10px", fontSize: 12, fontWeight: 600, color: "#1e293b", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 };
  const badge = { fontSize: 9.5, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: "#475569", background: "#e2e8f0", borderRadius: 5, padding: "1px 5px" };

  return (
    <div className="dpm-tile" style={card} onClick={onPreview} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPreview(); } }}
      aria-label={`Preview ${label}`}>
      <div style={media}>
        {image && thumb && !thumbErr
          ? <img src={thumb} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={() => setThumbErr(true)} />
          : <IcFile />}
      </div>
      <div className="dpm-tileov" style={ov}>
        <button type="button" className="dpm-btn" style={ovBtn} aria-label={`Preview ${label}`} title="Preview"
          onClick={(e) => { e.stopPropagation(); onPreview(); }}><IcEye /></button>
        {onDownload && <button type="button" className="dpm-btn" style={ovBtn} aria-label={`Download ${label}`} title="Download"
          onClick={(e) => { e.stopPropagation(); onDownload(); }}><IcDownload /></button>}
      </div>
      <div style={cap}>
        <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
        <span style={badge}>{ext || "file"}</span>
      </div>
    </div>
  );
}
